import React, { useMemo } from 'react';
import classNames from 'classnames';

import './switch.scss';

import { Appearance, HIGHLIGHT_COLOR } from '../../colors/colors';
import { useDragonfruit } from '../../contexts/DragonfruitProvider';
import { type DragonFruitIcon, DragonFruitIcons } from '../../icons/icons';
import type { UserInteractionProps } from '../../utils/user_interactions';
import Icon from '../Icon/Icon';
import Flex from '../Layout/Flex';
import type { MoreInfoIconProps } from '../MoreInfoIcon/MoreInfoIcon';
import MoreInfoIcon from '../MoreInfoIcon/MoreInfoIcon';

export interface SwitchProps extends UserInteractionProps {
  label?: string;
  secondaryLabel?: string;
  disabled?: boolean;
  onChange: (selected: boolean) => void;
  selected?: boolean;
  ariaLabel?: string;
  appearance?: Appearance;
  errorMessage?: string;
  warningMessage?: string;
  moreInfoIconProps?: MoreInfoIconProps;
  /** Render an Icon inside the switch toggle circle. */
  toggleIcon?: DragonFruitIcon;
}

const SWITCH_ICON_HEIGHT_PX = 8;

const Switch = (props: SwitchProps): React.JSX.Element => {
  const {
    selected,
    onChange,
    disabled,
    label,
    secondaryLabel,
    ariaLabel,
    appearance,
    errorMessage,
    warningMessage,
    moreInfoIconProps,
    toggleIcon,
    trackingId,
    eventData,
  } = props;
  const dragonfruit = useDragonfruit();
  const labelStyle = appearance === Appearance.HIGHLIGHT ? { color: HIGHLIGHT_COLOR } : {};

  function handleToggle(): void {
    onChange(!selected);
    dragonfruit?.trackUserInteraction?.({
      componentInteractionId: 'dragonfruit-switch-toggle',
      trackingId,
      eventData: {
        selected: !selected,
        ariaLabel,
        ...eventData,
      },
    });
  }

  /**
   * Determines whether the error or warning message should be shown and
   * returns the respective component to display it.
   */
  const errorWarningMessageComponent = useMemo((): JSX.Element | null | undefined => {
    if (disabled) {
      return null;
    } else if (errorMessage || warningMessage) {
      return (
        <div
          className={classNames('exception-message text-style-caption', {
            warning: warningMessage && !errorMessage,
            error: errorMessage,
          })}
        >
          {errorMessage || warningMessage}
        </div>
      );
    }
  }, [disabled, errorMessage, warningMessage]);

  return (
    <div className="dragonfruit-switch-wrapper">
      <div
        className={classNames('dragonfruit-switch-and-label-wrapper', { disabled })}
        onClick={disabled ? undefined : handleToggle}
        aria-label={label ?? ariaLabel}
        role="switch"
        aria-checked={Boolean(selected)}
      >
        <div className={classNames('dragonfruit-switch', { selected, disabled })}>
          <div className="switch-toggle">
            {toggleIcon && (
              <Icon src={DragonFruitIcons[toggleIcon]} height={SWITCH_ICON_HEIGHT_PX} />
            )}
          </div>
        </div>
        <Flex direction="column">
          {label && (
            <span className="switch-label text-style-title-body" style={labelStyle}>
              {label}
              {moreInfoIconProps && <MoreInfoIcon {...moreInfoIconProps} />}
            </span>
          )}
          {secondaryLabel && (
            <p
              className={classNames('text-style-title-body dragonfruit-switch-secondary-label', {
                disabled,
              })}
            >
              {secondaryLabel}
            </p>
          )}
        </Flex>
      </div>
      {errorWarningMessageComponent}
    </div>
  );
};

export default Switch;
